import React from 'react'
import { useState, useEffect, useMemo } from 'react'
// @ts-ignore
import { formatFloatAsCurrencySymbol } from '../../common/helpers.mjs'
// @ts-ignore
import { serviceDetails, currencyObject } from '../../common/types/types.d'
import { productType } from '../../types/types'
import { Button } from 'react-bootstrap'
import { useQuoteContext } from '../../hooks/useQuoteContext'

interface ServiceDetailsWithDynamicAccess extends serviceDetails {
   [key: string]: string | number
}

interface MultiProductDropdownTableProps {
   tableTitle: string
   productList: productType[]
   detailsList: ServiceDetailsWithDynamicAccess[]
   handleDetailsChange: (updatedList: ServiceDetailsWithDynamicAccess[]) => void
   currencyObject: currencyObject
   codeField: string
   quantityStep?: number
}

interface ProductTableRowProps {
   product: productType | undefined
   productDetails: ServiceDetailsWithDynamicAccess
   index: number
   codeField: string
   currencyObject: currencyObject
   quantityStep: number
   onProductChange: (value: string, productIndex: number) => void
   onQuantityChange: (value: number, index: number) => void
   onRemove: (index: number) => void
   getProductOptions: (
      currentProductCode: string,
   ) => { value: string; label: string }[]
}

// Custom hook for quantity input state management
export function useQuantityInput(
   initialValue: number,
   onUpdate: (value: number) => void,
) {
   const [localQuantity, setLocalQuantity] = useState<string>(
      initialValue.toString(),
   )

   // Update local state when external value changes
   useEffect(() => {
      setLocalQuantity(initialValue.toString())
   }, [initialValue])

   const handleChange = (inputValue: string) => {
      // Allow empty string
      if (inputValue === '') {
         setLocalQuantity('')
      } else {
         const newValue = parseFloat(inputValue)
         setLocalQuantity(isNaN(newValue) ? '' : newValue.toString())
      }
   }

   const handleBlur = (inputValue: string, step: number) => {
      const parsedValue = parseFloat(inputValue)
      // If the input is empty or results in NaN after blur, set to 0
      if (inputValue === '' || isNaN(parsedValue)) {
         onUpdate(0)
         setLocalQuantity('0')
         return
      }

      const remainder = parsedValue % step
      let flooredValue = parsedValue

      if (remainder !== 0) flooredValue = parsedValue - remainder

      const finalValue = flooredValue < 0 ? 0 : flooredValue
      onUpdate(finalValue)
      setLocalQuantity(finalValue.toString())
   }

   return {
      localQuantity,
      handleChange,
      handleBlur,
   }
}

// Pure function for generating product options - testable without React
export function generateProductOptions(
   productList: productType[],
   detailsList: ServiceDetailsWithDynamicAccess[],
   codeField: string,
   currentProductCode: string,
): { value: string; label: string }[] {
   // Remove options that have already been selected, except the current one
   const selectedCodes = detailsList
      .map((detail) => detail[codeField])
      .filter((code) => code !== currentProductCode)

   const noneOption = { value: 'None', label: 'None' }

   return [
      noneOption,
      ...productList
         .filter((product) => !selectedCodes.includes(product.ProductCode))
         .map((product) => ({
            value: product.ProductCode,
            label: product.ProductName,
         })),
   ]
}

// Pure function for handling product changes - testable without React
export function handleProductChange(
   detailsList: ServiceDetailsWithDynamicAccess[],
   codeField: string,
   value: string,
   productIndex: number,
): ServiceDetailsWithDynamicAccess[] {
   const updatedList = [...detailsList]
   updatedList[productIndex][codeField] = value
   return updatedList
}

// Pure function for handling quantity changes - testable without React
export function handleQuantityChange(
   detailsList: ServiceDetailsWithDynamicAccess[],
   value: number,
   index: number,
): ServiceDetailsWithDynamicAccess[] {
   const updatedList = [...detailsList]
   updatedList[index]['count'] = value
   return updatedList
}

// Pure function for removing items - testable without React
export function handleRemoveItem(
   detailsList: ServiceDetailsWithDynamicAccess[],
   index: number,
): ServiceDetailsWithDynamicAccess[] {
   const updatedList = [...detailsList]
   updatedList.splice(index, 1)
   return updatedList
}

// Pure function for calculating cost - testable without React
export function calculateProductCost(
   product:
      | productType
      | { ProductCode: string; ProductName: string; BasePrice: number }
      | undefined,
   quantity: number,
   currencyObject: currencyObject,
): string {
   if (!product || product.ProductCode === 'None') {
      return '$0.00'
   }
   const cost = (product.BasePrice || 0) * quantity
   return formatFloatAsCurrencySymbol(cost, currencyObject)
}

// Pure function for finding product by code - testable without React
export function findProductByCode(
   productList: productType[],
   productCode: string,
): productType | undefined {
   return productList.find((p) => p.ProductCode === productCode)
}

// Custom hook for product dropdown options (wrapper around pure function)
export function useProductOptions(
   productList: productType[],
   detailsList: ServiceDetailsWithDynamicAccess[],
   codeField: string,
) {
   const getProductOptions = (currentProductCode: string) => {
      return generateProductOptions(
         productList,
         detailsList,
         codeField,
         currentProductCode,
      )
   }

   return { getProductOptions }
}

// Extracted as a separate component for better testability
export function ProductTableRow({
   product,
   productDetails,
   index,
   codeField,
   currencyObject,
   quantityStep,
   onProductChange,
   onQuantityChange,
   onRemove,
   getProductOptions,
}: ProductTableRowProps) {
   const { getString } = useQuoteContext()
   const effectiveProduct =
      product ||
      (productDetails[codeField] === 'None'
         ? { ProductCode: 'None', ProductName: 'None', BasePrice: 0 }
         : undefined)
   if (!effectiveProduct) return null

   // Use the custom hook for quantity input management
   const { localQuantity, handleChange, handleBlur } = useQuantityInput(
      Number(productDetails.count),
      (value) => onQuantityChange(value, index),
   )

   return (
      <tr>
         <td>
            <select
               className='form-select-bhr2 integration-dropdown'
               value={effectiveProduct['ProductCode']}
               onChange={(e) => onProductChange(e.target.value, index)}
               data-testid={`product-select-${index}`}
            >
               {getProductOptions(effectiveProduct['ProductCode']).map(
                  (e, key) => {
                     return (
                        <option key={key} value={e.value}>
                           {e.label}
                        </option>
                     )
                  },
               )}
            </select>
         </td>
         <td>
            <input
               className='form-control input-field-0'
               type='number'
               min={0}
               step={quantityStep}
               value={localQuantity}
               disabled={effectiveProduct['ProductCode'] === 'None'}
               onChange={(e) => handleChange(e.target.value)}
               onBlur={(e) => handleBlur(e.target.value, quantityStep)}
               data-testid={`quantity-input-${index}`}
            />
         </td>
         <td data-testid={`cost-display-${index}`}>
            {calculateProductCost(
               effectiveProduct,
               Number(productDetails.count),
               currencyObject,
            )}
         </td>

         <td style={{ display: 'flex', justifyContent: 'end' }}>
            <Button
               variant='danger'
               className='add-remove-item-btn'
               size='sm'
               onClick={(e) => onRemove(index)}
               data-testid={`remove-button-${index}`}
            >
               {getString('global.Remove')}
            </Button>
         </td>
      </tr>
   )
}

function MultiProductDropdownTable({
   tableTitle,
   productList,
   detailsList,
   handleDetailsChange,
   currencyObject,
   codeField,
   quantityStep = 1,
}: MultiProductDropdownTableProps) {
   const { getString } = useQuoteContext()

   // Use the custom hook for product options
   const { getProductOptions } = useProductOptions(
      productList,
      detailsList,
      codeField,
   )

   // Use pure functions for handlers - better testability
   const onProductChange = (value: string, productIndex: number) => {
      const updatedList = handleProductChange(
         detailsList,
         codeField,
         value,
         productIndex,
      )
      handleDetailsChange(updatedList)
   }

   const onQuantityChange = (value: number, index: number) => {
      const updatedList = handleQuantityChange(detailsList, value, index)
      handleDetailsChange(updatedList)
   }

   const onRemove = (index: number) => {
      const updatedList = handleRemoveItem(detailsList, index)
      handleDetailsChange(updatedList)
   }

   return (
      <div
         style={{ padding: '0 10%' }}
         data-testid='multi-product-dropdown-table'
      >
         <table className='table table-sm'>
            <thead>
               <tr>
                  <th>{tableTitle}</th>
                  <th>{getString('global.Qty')}</th>
                  <th>{getString('global.Cost')}</th>
                  <th></th>
               </tr>
            </thead>
            <tbody>
               {Array.from({ length: detailsList.length }, (_, i) => i).map(
                  (i) => (
                     <ProductTableRow
                        key={i}
                        index={i}
                        product={findProductByCode(
                           productList,
                           detailsList[i][codeField] as string,
                        )}
                        productDetails={detailsList[i]}
                        codeField={codeField}
                        currencyObject={currencyObject}
                        quantityStep={quantityStep}
                        onProductChange={onProductChange}
                        onQuantityChange={onQuantityChange}
                        onRemove={onRemove}
                        getProductOptions={getProductOptions}
                     />
                  ),
               )}
            </tbody>
         </table>
      </div>
   )
}

export default MultiProductDropdownTable
